import { useState, useEffect, useContext, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { AuthContext } from '../contexts/AuthContext';
import { accountService } from '../services/accountService';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import ConfirmationModal from '../components/ConfirmationModal';
import MaxBadge from '../components/MaxBadge';
import UpgradeModal from '../components/UpgradeModal';
import { licenseView } from '../utils/licenses';
import { captureArrivalCodes } from '../lib/arrival-codes';
import { hasCodeParams, stripCodeParams, NO_CODES } from '../lib/codes';
import type { ArrivalCodes } from '../lib/codes';

let stripePromise: Promise<Stripe | null> | null = null;

type PlanKey = 'annual' | 'monthly';
type TierKey = 'pro' | 'max';
/** One volume band of a tiered price (dollars per seat; upTo null = the open-ended top band). */
type PriceTierBand = { upTo: number | null; unitAmount: number | null };
type PlanPrice = {
  amount: number;
  currency?: string;
  formatted: string;
  interval?: string;
  /** Sept 2026 volume pricing: the single-seat rate in dollars. */
  perSeatAmount?: number | null;
  /** Sept 2026 volume pricing: the band table; null on flat per_unit prices (all Pro prices). */
  tiers?: PriceTierBand[] | null;
} | null;
type PlansShape = {
  annual: PlanPrice;
  monthly: PlanPrice;
  defaultPlan?: PlanKey;
  annualSavingsPercent?: number | null;
};
/** EPIC-07: per-tier price matrix from GET /api/stripe/config `tiers`. */
type TiersShape = {
  pro?: { annual?: PlanPrice; monthly?: PlanPrice };
  max?: { annual?: PlanPrice; monthly?: PlanPrice };
} | null;

/**
 * The Max feature line, translated at render. It is a hook rather than a module
 * constant because the message has to reach the catalog through the macro.
 */
function useMaxFeatureLine(): string {
  const { t } = useLingui();
  return t`Private MCP, Privacy Agents, IP Connections, agent governance, Multiplayer Memory`;
}

function tierPrice(tiers: TiersShape, tier: TierKey, plan: PlanKey): PlanPrice {
  return tiers?.[tier]?.[plan] || null;
}

/** "$34" / "$7.50": whole dollars without cents, fractional with. */
function moneyLabel(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

/**
 * Per-seat monthly amount derived from the tiers config (annual prices are the
 * full-year amount). Prices always come from the config; when a price is
 * missing we return null and the caller shows a dash rather than any stale
 * hardcoded fallback.
 */
function perMonthAmount(price: PlanPrice, plan: PlanKey): string | null {
  if (!price || typeof price.amount !== 'number' || price.amount <= 0) return null;
  const monthly = plan === 'annual' ? price.amount / 12 : price.amount;
  return moneyLabel(monthly);
}

/** The per-seat monthly rate, with the wording that goes around it. */
function PerMonthLabel({ price, plan }: { price: PlanPrice; plan: PlanKey }) {
  const amountStr = perMonthAmount(price, plan);
  if (!amountStr) return <>—</>;
  return plan === 'annual'
    ? <Trans>{amountStr}/mo billed annually</Trans>
    : <Trans>{amountStr}/mo</Trans>;
}

/**
 * Compact volume band table for a tiered price (Sept 2026 Max pricing). Bands
 * come straight from the config in dollars per seat; annual bands carry the
 * full-year amount and render as the per-month equivalent. All seats bill at
 * the band the total seat count lands in (Stripe volume mode).
 */
function VolumeTierTable({ price, plan }: { price: PlanPrice; plan: PlanKey }) {
  const { t } = useLingui();
  const bands = price?.tiers;
  if (!bands || bands.length === 0) return null;

  const rows: { key: string; range: string; rate: string }[] = [];
  bands.forEach((band, i) => {
    if (typeof band.unitAmount !== 'number') return;
    const start = i === 0 ? 1 : (bands[i - 1]?.upTo ?? 0) + 1;
    const range = band.upTo === null
      ? `${start}+`
      : start >= band.upTo ? `${band.upTo}` : `${start}-${band.upTo}`;
    const monthly = plan === 'annual' ? band.unitAmount / 12 : band.unitAmount;
    rows.push({ key: `${range}`, range: t`${range} seats`, rate: t`${moneyLabel(monthly)}/mo` });
  });
  if (rows.length === 0) return null;

  return (
    <div className="mt-3 pt-2 text-xs space-y-0.5" style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
      {rows.map((row) => (
        <div key={row.key} className="flex items-center justify-between gap-3">
          <span>{row.range}</span>
          <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>{row.rate}</span>
        </div>
      ))}
      <div className="pt-1">
        {plan === 'annual'
          ? <Trans>Per seat, billed annually. All seats bill at your band.</Trans>
          : <Trans>Per seat per month. All seats bill at your band.</Trans>}
      </div>
    </div>
  );
}

/**
 * Pro vs Max product choice (EPIC-07 7.4), shown above the billing toggle.
 *
 * Sept 2026 pricing: Pro is an individual account. Corporate-domain checkouts
 * default to Max and show `proHint` under the Pro card, but Pro stays
 * selectable: a domain's sole user may buy it, and the server is the
 * authority (409 PRO_INDIVIDUAL_ONLY on domains with more than one user).
 */
function PlanToggle({
  plans,
  selected,
  onChange
}: {
  plans: PlansShape | null;
  selected: PlanKey;
  onChange: (p: PlanKey) => void;
}) {
  const { t } = useLingui();
  if (!plans) return null;
  const annual = plans.annual;
  const monthly = plans.monthly;
  const savings = plans.annualSavingsPercent;

  const Card = ({ plan, label, price, badge }: {
    plan: PlanKey;
    label: string;
    price: PlanPrice;
    badge?: string;
  }) => (
    <button
      type="button"
      onClick={() => onChange(plan)}
      className={`text-left flex-1 p-4 rounded-lg transition-all ${
        selected === plan ? 'ring-2 ring-blue-500' : 'opacity-80 hover:opacity-100'
      }`}
      style={{
        border: '1px solid var(--color-border)',
        backgroundColor: selected === plan ? 'var(--color-surface-secondary)' : 'var(--color-surface)'
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold">{label}</span>
        {badge && (
          <span className="text-xs font-semibold text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-500/15 px-2 py-0.5 rounded">
            {badge}
          </span>
        )}
      </div>
      <div className="text-lg font-semibold">{price?.formatted || '—'}</div>
    </button>
  );

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <Card
        plan="annual"
        label={t`Annual`}
        price={annual}
        badge={savings ? t`Save ${savings}%` : t`Best value`}
      />
      <Card plan="monthly" label={t`Monthly`} price={monthly} />
    </div>
  );
}

function SubscriptionCheckout({
  plans,
  tiers,
  defaultPlan
}: {
  plans: PlansShape | null;
  tiers: TiersShape;
  defaultPlan: PlanKey;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { isDark } = useTheme();
  const { t } = useLingui();
  const maxFeatureLine = useMaxFeatureLine();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [plan, setPlan] = useState<PlanKey>(defaultPlan);
  // EPIC-15 (PLG): Pro is free and not sellable; the only checkout is Max,
  // charged immediately (the server refuses anything else with PRO_IS_FREE).
  const tier: TierKey = 'max';
  // Re-entry guard: setLoading is async, so a fast double-click can enter
  // handleSubmit twice before the button disables. Each entry would mint a
  // separate payment method (different id -> different server idempotency key)
  // and could create two subscriptions. This ref blocks the second entry
  // synchronously.
  const submittingRef = useRef(false);

  // Price for the selected tier and interval, always from the config. The Max
  // tier never falls back to the legacy single-product price (that price is
  // Pro); a missing price renders as a dash rather than a stale number.
  // Max never falls back to the legacy single-product price (that price is
  // Pro); a missing price renders as a dash rather than a stale number.
  const selectedPrice: PlanPrice = tierPrice(tiers, tier, plan);

  // Billing-interval toggle prices follow the selected tier when the per-tier
  // config is available; otherwise the legacy Pro plans shape is shown.
  const tierAnnual = tierPrice(tiers, tier, 'annual');
  const tierMonthly = tierPrice(tiers, tier, 'monthly');
  const togglePlans: PlansShape | null = (tierAnnual || tierMonthly)
    ? {
        annual: tierAnnual,
        monthly: tierMonthly,
        defaultPlan: plans?.defaultPlan,
        annualSavingsPercent:
          tierAnnual?.amount && tierMonthly?.amount
            ? Math.round((1 - tierAnnual.amount / 12 / tierMonthly.amount) * 100)
            : plans?.annualSavingsPercent,
      }
    : plans;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    setError('');
    setLoading(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error(t`Card element not found`);
      }

      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError || !paymentMethod) {
        throw new Error(pmError?.message || t`Failed to create payment method`);
      }

      const result = await accountService.createSubscription(
        paymentMethod.id,
        promoCode.trim() || undefined,
        plan,
        tier
      );
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
      } else {
        throw new Error(result.error || t`Failed to create subscription`);
      }
    } catch (err: any) {
      const errorMessage = err.message || t`Payment failed`;
      setError(errorMessage);
      if (errorMessage.toLowerCase().includes('promo') || errorMessage.toLowerCase().includes('coupon') || errorMessage.toLowerCase().includes('code')) {
        setPromoCodeError(errorMessage);
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  if (success) {
    return (
      <div className="glass-panel rounded-2xl p-8 sm:p-12 text-center">
        <div className="text-5xl mb-4">✓</div>
        <h2 className="text-2xl font-semibold mb-2"><Trans>Subscription Activated!</Trans></h2>
        <p style={{ color: 'var(--color-text-muted)' }}><Trans>Redirecting to dashboard...</Trans></p>
      </div>
    );
  }

  const cardStyle = {
    base: {
      fontSize: '16px',
      color: isDark ? '#e2e8f0' : '#0f172a',
      fontFamily: "'Inter', sans-serif",
      '::placeholder': {
        color: isDark ? '#64748b' : '#94a3b8',
      },
    },
    invalid: {
      color: '#ef4444',
    },
  };

  // Product names stay as they are; only the billing interval is translated.
  const tierName = tier === 'max' ? 'Max' : 'Pro';
  const planName = plan === 'annual' ? t`Annual` : t`Monthly`;

  return (
    <form onSubmit={handleSubmit}>
      <div className="glass-panel rounded-2xl p-6 sm:p-8 space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2 flex items-center gap-2"><Trans>Subscribe to PrivacyPal Max</Trans> <MaxBadge /></h2>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>Charged today. No trial period. The free plan stays yours if you ever cancel.</Trans>
          </p>
        </div>

        <VolumeTierTable price={tierPrice(tiers, 'max', plan)} plan={plan} />

        <PlanToggle plans={togglePlans} selected={plan} onChange={setPlan} />

        {error && (
          <div className="p-4 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-risk-bg)', border: '1px solid var(--color-risk-text)', color: 'var(--color-risk-text)' }} role="alert">
            <p className="font-medium mb-0.5"><Trans>We couldn&apos;t complete your subscription</Trans></p>
            <p className="mb-0">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}><Trans>Card Details</Trans></label>
          <div className="p-4 rounded-lg" style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
            <CardElement options={{ style: cardStyle }} />
          </div>
        </div>

        <div>
          {!showPromoCode ? (
            <button
              type="button"
              onClick={() => setShowPromoCode(true)}
              className="text-sm underline"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <Trans>Have a promo code?</Trans>
            </button>
          ) : (
            <div>
              <label className="block text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}><Trans>Promo Code</Trans></label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoCodeError('');
                  }}
                  placeholder={t`Enter promo code`}
                  className="flex-1 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                />
                <button
                  type="button"
                  onClick={() => { setShowPromoCode(false); setPromoCode(''); setPromoCodeError(''); }}
                  className="px-4 py-2 text-sm"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <Trans>Cancel</Trans>
                </button>
              </div>
              {promoCodeError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{promoCodeError}</p>}
            </div>
          )}
        </div>

        <div
          className="p-4 sm:p-6 rounded-lg"
          style={{
            backgroundColor: 'var(--color-surface-secondary)',
            border: tier === 'max' ? '1px solid var(--color-max-border)' : '1px solid var(--color-border)'
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
            <div>
              <div className="text-lg font-semibold flex items-center gap-2">
                PrivacyPal {tier === 'max' ? 'Max' : 'Pro'}: {plan === 'annual' ? <Trans>Annual</Trans> : <Trans>Monthly</Trans>}
                {tier === 'max' && <MaxBadge />}
              </div>
              <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>{maxFeatureLine}. Billed today, then every renewal until canceled.</Trans>
              </div>
            </div>
            <div className="text-2xl font-semibold">
              {selectedPrice ? selectedPrice.formatted : '—'}
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-3 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
          disabled={loading || !stripe}
        >
          {loading ? t`Processing...` : t`Subscribe now: ${tierName} ${planName}`}
        </button>
      </div>
    </form>
  );
}

function PaymentMethodUpdate({ 
  subscriptionDetails, 
  onUpdate 
}: { 
  subscriptionDetails: any;
  onUpdate: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { showToast } = useToast();
  const { isDark } = useTheme();
  const { t } = useLingui();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpdateForm, setShowUpdateForm] = useState(false);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setError('');
    setLoading(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error(t`Card element not found`);
      }

      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError || !paymentMethod) {
        throw new Error(pmError?.message || t`Failed to create payment method`);
      }

      await accountService.updatePaymentMethod(paymentMethod.id);
      showToast(t`Payment method updated successfully`, 'success');
      setShowUpdateForm(false);
      onUpdate();
    } catch (err: any) {
      setError(err.message || t`Failed to update payment method`);
      showToast(err.message || t`Failed to update payment method`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    base: {
      fontSize: '16px',
      color: isDark ? '#e2e8f0' : '#0f172a',
      fontFamily: "'Inter', sans-serif",
      '::placeholder': { color: isDark ? '#64748b' : '#94a3b8' },
    },
    invalid: { color: '#ef4444' },
  };

  if (!showUpdateForm) {
    return (
      <div className="glass-panel rounded-2xl p-5 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold mb-1"><Trans>Payment Method</Trans></h3>
            {subscriptionDetails.paymentMethod ? (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {(subscriptionDetails.paymentMethod.brand || t`Card`).toString().toUpperCase()} •••• {subscriptionDetails.paymentMethod.last4 || '****'}
                  </span>
                  {subscriptionDetails.paymentMethod.expMonth && subscriptionDetails.paymentMethod.expYear && (
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      <Trans>Expires {subscriptionDetails.paymentMethod.expMonth}/{subscriptionDetails.paymentMethod.expYear}</Trans>
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}><Trans>No payment method on file</Trans></p>
            )}
          </div>
          <button
            onClick={() => setShowUpdateForm(true)}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-all self-start sm:self-auto"
            style={{ border: '1px solid var(--color-border)' }}
          >
            <Trans>Change</Trans>
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleUpdate} className="glass-panel rounded-2xl p-5 sm:p-6 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold"><Trans>Update Payment Method</Trans></h3>
        <button
          type="button"
          onClick={() => { setShowUpdateForm(false); setError(''); }}
          className="text-sm"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <Trans>Cancel</Trans>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-risk-bg)', border: '1px solid var(--color-risk-text)', color: 'var(--color-risk-text)' }}>
          {error}
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}><Trans>New Card Details</Trans></label>
        <div className="p-4 rounded-lg" style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
          <CardElement options={{ style: cardStyle }} />
        </div>
      </div>

      <button
        type="submit"
        className="w-full py-3 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
        disabled={loading || !stripe}
      >
        {loading ? t`Updating...` : t`Update Payment Method`}
      </button>
    </form>
  );
}

export default function SubscriptionPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useLingui();
  const maxFeatureLine = useMaxFeatureLine();
  const [stripeConfig, setStripeConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>(null);
  const [account, setAccount] = useState<any>(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // Personal-account Upgrade to Max (EPIC-07 7.4)
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  // EPIC-15: shared Max checkout; ?upgrade=max deep-links it open (the
  // desktop app's upsell notifications land here).
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // The desktop app's Max dialog opens this page with the codes it collected:
  // /subscription?upgrade=max&promo=CODE&trial=CODE&src=desktop-...
  // They must be captured BEFORE the address bar is cleaned, otherwise an
  // enterprise trial code entered in the app lands on a card form, which is
  // the exact opposite of what that code means.
  const [arrivalCodes, setArrivalCodes] = useState<ArrivalCodes>(NO_CODES);
  const [arrivalSource, setArrivalSource] = useState<string>('subscription');

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const codes = captureArrivalCodes(search);
    setArrivalCodes(codes);
    const src = params.get('src');
    if (src) setArrivalSource(src);

    const wantsUpgrade = params.get('upgrade') === 'max';
    if (wantsUpgrade) setUpgradeOpen(true);

    // One-shot: drop upgrade and the code params so a refresh or a back does
    // not reopen the modal or re-apply a spent code. stripCodeParams keeps
    // utm_* and anything else, so campaign attribution survives.
    if (wantsUpgrade || hasCodeParams(search) || src) {
      params.delete('upgrade');
      params.delete('src');
      const kept = stripCodeParams(`?${params.toString()}`);
      window.history.replaceState(null, '', window.location.pathname + kept);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [config, companyData, accountData] = await Promise.all([
        accountService.getStripeConfig(),
        accountService.getCompany(),
        accountService.getAccountStatus()
      ]);
      
      setCompany(companyData);
      setAccount(accountData);

      if (!accountData?.isAdmin) {
        const isPro = accountData?.status === 'pro';
        if (isPro) {
          navigate('/dashboard');
          return;
        }
      }

      const isPro = accountData?.status === 'pro';
      if (accountData?.isAdmin && isPro) {
        try {
          const details = await accountService.getSubscriptionDetails();
          if (details && details.success) {
            setSubscriptionDetails(details);
          }
        } catch (err: any) {
          console.error('Failed to load subscription details:', err);
        }
      }
      
      if (config?.publishableKey) {
        setStripeConfig(config);
        stripePromise = loadStripe(config.publishableKey);
      } else if (config) {
        // Even without a publishable key, keep the plan metadata so the UI can
        // describe the offering; checkout will surface a clearer error.
        setStripeConfig(config);
      }
    } catch (err: any) {
      console.error('Failed to load subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelConfirm(true);
  };

  const confirmUpgradeToMax = async () => {
    setShowUpgradeConfirm(false);
    setUpgrading(true);
    setUpgradeError('');
    try {
      await accountService.updateSubscriptionTier('max');
      showToast(t`Welcome to PrivacyPal Max`, 'success');
      await loadData();
    } catch (err: any) {
      if (err.response?.status === 402) {
        // Payment failed; PaymentMethodUpdate lives further down this page.
        setUpgradeError(t`Payment failed. Update the payment method below, then try the upgrade again.`);
      } else {
        setUpgradeError(err.response?.data?.error || t`Failed to upgrade to Max. Please try again.`);
      }
    } finally {
      setUpgrading(false);
    }
  };

  const confirmCancelSubscription = async () => {
    setShowCancelConfirm(false);
    setCancelling(true);
    try {
      if (company && !company.isPersonal && account?.isAdmin) {
        await accountService.cancelCompanySubscription();
      } else {
        await accountService.cancelSubscription();
      }
      await loadData();
      const trialing = account?.status === 'trial' || account?.effectiveStatus === 'trial';
      const msg = trialing
        ? t`Your trial has been cancelled. You won't be charged.`
        : t`Your subscription has been cancelled. You will have access until the end of your current billing period.`;
      showToast(msg, 'success');
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      showToast(error.response?.data?.error || t`Failed to cancel subscription. Please try again.`, 'error');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!account?.isAdmin) {
    const isPro = account?.status === 'pro';
    if (isPro) {
      return (
        <Layout>
          <div className="glass-panel rounded-2xl p-6 sm:p-8">
            <p className="text-red-600 dark:text-red-400 mb-4">
              <Trans>You don't have access to subscription management. Only administrators can manage subscriptions.</Trans>
            </p>
            <button
              className="px-4 py-2 text-sm font-medium rounded-full transition-all"
              style={{ border: '1px solid var(--color-border)' }}
              onClick={() => navigate('/dashboard')}
            >
              <Trans>Back to Dashboard</Trans>
            </button>
          </div>
        </Layout>
      );
    }
  }

  const isPro = account?.status === 'pro';

  // EPIC-15 (PLG): a status-'pro' account with no billing anywhere is the
  // FREE plan, not a subscription to manage. Renders its own panel: what the
  // free plan protects, and the Max upgrade.
  const hasRealSubscription =
    !!account?.subscriptionId ||
    subscriptionDetails?.success === true ||
    (company && !company.isPersonal && company?.subscriptionStatus === 'active');

  if (isPro && !hasRealSubscription) {
    const grantedMax = account?.effectiveTier === 'max';
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold mb-2"><Trans>Subscription</Trans></h1>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}><Trans>Your plan and upgrade options</Trans></p>
          </div>

          <div className="glass-panel rounded-2xl p-5 sm:p-8 space-y-4">
            <div>
              <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Current Plan</Trans></p>
              <h2 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                {grantedMax ? <><Trans>PrivacyPal Max</Trans> <MaxBadge /></> : <Trans>PrivacyPal Pro, free</Trans>}
              </h2>
            </div>
            <p className="text-sm mb-0" style={{ color: 'var(--color-text-secondary)' }}>
              {grantedMax
                ? <Trans>Your Max access is managed by PrivacyPal. There is nothing to pay here.</Trans>
                : <Trans>Real-time protection for ChatGPT, Claude, Gemini, Grok and Perplexity chats, attachments included. No card on file, nothing to cancel, free forever.</Trans>}
            </p>
          </div>

          {!grantedMax && (
          <div
            className="glass-panel rounded-2xl p-5 sm:p-8 space-y-4"
            style={{ border: '1px solid var(--color-max-border)', boxShadow: '0 8px 32px 0 var(--color-max-glow)' }}
          >
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold"><Trans>PrivacyPal Max</Trans></h2>
              <MaxBadge />
            </div>
            <ul className="text-sm space-y-1.5 mb-0" style={{ color: 'var(--color-text-secondary)', listStyle: 'none', padding: 0 }}>
              <li><Trans>Protection on every AI surface: Cowork, Claude Code, Codex, Copilot, Grok Bot, Hermes and the OpenAI, Anthropic and xAI APIs</Trans></li>
              <li><Trans>Connections, Private MCP and API access</Trans></li>
              <li><Trans>Team seats, central provisioning and volume pricing</Trans></li>
              <li><Trans>Full usage history and team activity</Trans></li>
            </ul>
            <button
              onClick={() => setUpgradeOpen(true)}
              className="px-6 py-2.5 text-sm font-semibold rounded-lg text-white transition-all hover:shadow-lg"
              style={{ backgroundColor: 'var(--color-max-accent)' }}
            >
              <Trans>Upgrade to Max</Trans>
            </button>
          </div>
          )}
        </div>

        <UpgradeModal
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          onUpgraded={() => loadData()}
          source={arrivalSource}
          initialPromoCode={arrivalCodes.promoCode}
          initialTrialCode={arrivalCodes.trialCode}
          initialPath={arrivalCodes.trialCode ? 'trial' : 'card'}
        />
      </Layout>
    );
  }

  if (account?.isAdmin && isPro && hasRealSubscription) {
    const isCompanySubscription = subscriptionDetails?.type === 'company' || (company && !company.isPersonal);
    const isCanceled = subscriptionDetails?.cancelAtPeriodEnd ||
                       subscriptionDetails?.status === 'canceled' ||
                       account?.subscriptionStatus === 'canceled' ||
                       company?.subscriptionStatus === 'canceled';

    const intervalLabel = subscriptionDetails?.interval === 'year'
      ? 'year'
      : subscriptionDetails?.interval === 'month'
      ? 'month'
      : subscriptionDetails?.plan === 'annual'
      ? 'year'
      : 'month';
    const billLabel = intervalLabel === 'year' ? t`Annual Bill` : t`Monthly Bill`;
    // intervalLabel stays the untranslated key the comparisons above rely on;
    // these two carry the wording that reaches the page.
    const intervalName = intervalLabel === 'year' ? t`year` : t`month`;
    const intervalWord = intervalLabel === 'year' ? t`annual` : t`monthly`;

    const totalDollars = typeof subscriptionDetails?.amount === 'number'
      ? subscriptionDetails.amount / 100
      : null;
    const baseDollars = typeof subscriptionDetails?.baseAmount === 'number'
      ? subscriptionDetails.baseAmount / 100
      : null;
    const discountDollars = typeof subscriptionDetails?.discountAmount === 'number'
      ? subscriptionDetails.discountAmount / 100
      : null;

    // EPIC-07: tier display + per-tier license counts. The headline is the
    // summed quantity across tiers; the split renders when the details payload
    // carries per-tier quantities (quantityPro/quantityMax from
    // GET /api/stripe/subscription/details), with the company prepaid seat
    // block (GET /api/company) as the fallback. licenseView normalizes every
    // source, so no count can render as undefined or NaN, and only the tiers
    // that actually hold seats get a line ("1 Max", not "0 Pro · 1 Max").
    const isMaxTier = account?.effectiveTier === 'max' || subscriptionDetails?.tier === 'max';
    const tierLabel = isMaxTier ? 'Max' : 'Pro';
    const licenses = licenseView(subscriptionDetails, company, { allowCompanyFallback: isCompanySubscription });
    const isPersonalSubscription = !isCompanySubscription;
    const couponName = subscriptionDetails?.discount?.coupon?.name || t`Promo Code`;
    const baseRateLabel = baseDollars?.toFixed(2) || t`full price`;

    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold mb-2"><Trans>Subscription</Trans></h1>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {isCompanySubscription
                ? <Trans>Manage your company {tierLabel} subscription</Trans>
                : <Trans>Manage your {tierLabel} subscription</Trans>}
            </p>
          </div>

          <div className="glass-panel rounded-2xl p-5 sm:p-8 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 gap-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Current Subscription</Trans></p>
                <h2 className="text-xl sm:text-2xl font-semibold flex items-center flex-wrap gap-2">
                  PrivacyPal {tierLabel}
                  {isMaxTier && <MaxBadge />}
                  {subscriptionDetails?.plan && (
                    <span className="text-sm font-normal" style={{ color: 'var(--color-text-secondary)' }}>
                      {subscriptionDetails.plan === 'annual' ? <Trans>Annual</Trans> : <Trans>Monthly</Trans>}
                    </span>
                  )}
                  {isCanceled && (
                    <span className="text-sm font-normal text-red-600 dark:text-red-400"><Trans>(Canceling)</Trans></span>
                  )}
                </h2>
              </div>
              {!isCanceled && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-all disabled:opacity-50 self-start"
                >
                  {cancelling ? t`Cancelling...` : t`Cancel Subscription`}
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Licenses</Trans></p>
                <p className="text-lg font-semibold">{licenses.total}</p>
                {licenses.showSplit && licenses.splitParts.length > 0 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    {licenses.splitParts.map((part, index) => (
                      <span key={part.tier}>
                        {index > 0 && ' · '}
                        <span style={part.tier === 'max' ? { color: 'var(--color-max-text)' } : undefined}>
                          {part.seats} {part.label}
                        </span>
                      </span>
                    ))}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>
                  {isCanceled ? <Trans>Plan Ends</Trans> : <Trans>Next Billing Cycle</Trans>}
                </p>
                <p className="text-lg font-semibold">
                  {subscriptionDetails?.currentPeriodEnd
                    ? new Date(subscriptionDetails.currentPeriodEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                    : account?.subscriptionEndDate
                    ? new Date(account.subscriptionEndDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                    : <Trans>N/A</Trans>}
                </p>
              </div>
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>{billLabel}</p>
                <div>
                  {totalDollars !== null ? (
                    <div>
                      {subscriptionDetails?.discount?.coupon?.percent_off && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-2 py-1 rounded">
                            <Trans>{subscriptionDetails.discount.coupon.percent_off}% OFF</Trans>
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <p className={`text-lg font-semibold ${subscriptionDetails?.discount ? 'text-green-600 dark:text-green-400' : ''}`}>
                          ${totalDollars.toFixed(2)} / {intervalName}
                        </p>
                      </div>
                      {baseDollars !== null && baseDollars > totalDollars && (
                        <p className="text-xs line-through mt-1" style={{ color: 'var(--color-text-muted)' }}>
                          ${baseDollars.toFixed(2)} / {intervalName}
                        </p>
                      )}
                      {subscriptionDetails?.discount?.end && (
                        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                          <Trans>Discount expires {new Date(subscriptionDetails.discount.end).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</Trans>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-lg font-semibold" style={{ color: 'var(--color-text-muted)' }}>—</p>
                  )}
                </div>
              </div>
            </div>

            {subscriptionDetails?.discount && (
              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-code-bg)', border: '1px solid var(--color-code-border)' }}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold mb-1">
                      <Trans>Active Discount: {couponName}</Trans>
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {subscriptionDetails.discount.coupon?.percent_off
                        ? <Trans>You're saving {subscriptionDetails.discount.coupon.percent_off}% off your subscription</Trans>
                        : discountDollars
                        ? <Trans>You're saving ${discountDollars.toFixed(2)} per {intervalName}</Trans>
                        : <Trans>Discount applied to your subscription</Trans>}
                      {subscriptionDetails.discount.end && (
                        <span className="block mt-1">
                          <Trans>This discount expires on {new Date(subscriptionDetails.discount.end).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}. After that, your rate will return to ${baseRateLabel} / {intervalName}.</Trans>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Personal accounts: Upgrade to Max (EPIC-07 7.4) */}
          {isPersonalSubscription && !isMaxTier && !isCanceled && (
            <div
              className="glass-panel rounded-2xl p-5 sm:p-6"
              style={{ border: '1px solid var(--color-max-border)', boxShadow: '0 8px 32px 0 var(--color-max-glow)' }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold">PrivacyPal Max</h3>
                    <MaxBadge />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {maxFeatureLine}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    <PerMonthLabel
                      price={tierPrice(stripeConfig?.tiers || null, 'max', intervalLabel === 'year' ? 'annual' : 'monthly')}
                      plan={intervalLabel === 'year' ? 'annual' : 'monthly'}
                    />
                  </p>
                </div>
                <button
                  onClick={() => { setUpgradeError(''); setShowUpgradeConfirm(true); }}
                  disabled={upgrading}
                  className="self-start sm:self-auto px-5 py-2.5 text-sm font-semibold rounded-lg text-white transition-all hover:shadow-lg disabled:opacity-50 whitespace-nowrap"
                  style={{ backgroundColor: 'var(--color-max-accent)' }}
                >
                  {upgrading ? t`Upgrading...` : t`Upgrade to Max`}
                </button>
              </div>
              {upgradeError && (
                <div className="mt-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-risk-bg)', border: '1px solid var(--color-risk-text)', color: 'var(--color-risk-text)' }} role="alert">
                  {upgradeError}
                </div>
              )}
            </div>
          )}

          {stripePromise && stripeConfig?.publishableKey && (
            <Elements stripe={stripePromise}>
              <PaymentMethodUpdate
                subscriptionDetails={subscriptionDetails || {}}
                onUpdate={loadData}
              />
            </Elements>
          )}
        </div>

        <ConfirmationModal
          isOpen={showCancelConfirm}
          title={t`Cancel Subscription`}
          message={t`Are you sure you want to cancel your subscription? You will have access until the end of your current billing period.`}
          confirmText={t`Cancel Subscription`}
          cancelText={t`Keep Subscription`}
          onConfirm={confirmCancelSubscription}
          onCancel={() => setShowCancelConfirm(false)}
          variant="danger"
        />

        <ConfirmationModal
          isOpen={showUpgradeConfirm}
          title={t`Upgrade to PrivacyPal Max`}
          message={t`Your subscription switches to PrivacyPal Max on your current ${intervalWord} interval. The prorated difference for the rest of the current billing period is charged to your payment method immediately.`}
          confirmText={t`Upgrade to Max`}
          cancelText={t`Not now`}
          onConfirm={confirmUpgradeToMax}
          onCancel={() => setShowUpgradeConfirm(false)}
          variant="default"
        />
      </Layout>
    );
  }

  const isTrialExpired = () => {
    const hasActiveSubscription =
      account?.status === 'pro' ||
      account?.effectiveStatus === 'pro' ||
      account?.subscriptionStatus === 'active' ||
      company?.subscriptionStatus === 'active';
    
    if (hasActiveSubscription || !account?.trialEndDate) {
      return false;
    }
    
    try {
      const trialEndDate = typeof account.trialEndDate === 'string' 
        ? new Date(account.trialEndDate)
        : new Date(
            account.trialEndDate.year || new Date().getFullYear(),
            (account.trialEndDate.month || 1) - 1,
            account.trialEndDate.day || 1,
            account.trialEndDate.hour || 0,
            account.trialEndDate.minute || 0,
            account.trialEndDate.second || 0
          );
      return trialEndDate < new Date();
    } catch (e) {
      console.error('Error checking trial expiration:', e);
      return false;
    }
  };

  const trialExpired = isTrialExpired();

  const trialExpiredBanner = trialExpired && (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-500/10 dark:to-purple-500/10 border border-indigo-200 dark:border-indigo-500/30 rounded-lg p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-300 mb-1"><Trans>Your Trial Has Ended</Trans></h3>
          <p className="text-sm text-indigo-700 dark:text-indigo-400 mb-0">
            <Trans>Thank you for trying PrivacyPal! Enter your payment method below to continue protecting your data.</Trans>
          </p>
        </div>
      </div>
    </div>
  );

  if (company && !company.isPersonal && account?.isAdmin) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold mb-2"><Trans>Company Subscription</Trans></h1>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}><Trans>Manage your company subscription</Trans></p>
          </div>

          {trialExpiredBanner}

          {company.stripeSubscriptionId && company.subscriptionStatus === 'active' ? (
            <div className="glass-panel rounded-2xl p-6 sm:p-8">
              <div className="text-center">
                <div className="text-5xl mb-4">✓</div>
                <h2 className="text-xl font-semibold mb-2"><Trans>Active Subscription</Trans></h2>
                <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                  <Trans>Your company has an active subscription. You can manage users and licenses from the User Management page.</Trans>
                </p>
                <button
                  onClick={() => navigate('/users')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Trans>Go to User Management</Trans>
                </button>
              </div>
            </div>
          ) : (
            <div className="glass-panel rounded-2xl p-6 sm:p-8">
              <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                <Trans>Set up a subscription for your company. This will allow you to add licenses for your team members.
                Teams run on PrivacyPal Max: the per-seat price drops automatically as your team grows.</Trans>
              </p>
              {stripePromise && stripeConfig?.publishableKey && (
                <Elements stripe={stripePromise}>
                  <SubscriptionCheckout
                    plans={stripeConfig?.plans || null}
                    tiers={stripeConfig?.tiers || null}
                    defaultPlan={(stripeConfig?.defaultPlan as PlanKey) || 'annual'}
                  />
                </Elements>
              )}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // Actively trialing (card on file, trial not yet charged, not already canceling).
  // These users need a way to cancel before the day-5 charge; otherwise their only
  // recourse is a chargeback.
  const isActivelyTrialing =
    !trialExpired &&
    (account?.status === 'trial' || account?.effectiveStatus === 'trial') &&
    account?.subscriptionStatus !== 'canceled';

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold mb-2"><Trans>Subscription</Trans></h1>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}><Trans>Choose your plan for unlimited access</Trans></p>
        </div>

        {trialExpiredBanner}

        {isActivelyTrialing && (
          <div className="glass-panel rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium mb-1"><Trans>You're on a free trial</Trans></p>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>Cancel before your trial ends and you won't be charged.</Trans>
              </p>
            </div>
            <button
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-all disabled:opacity-50 self-start whitespace-nowrap"
            >
              {cancelling ? t`Cancelling...` : t`Cancel Trial`}
            </button>
          </div>
        )}

        {stripePromise && stripeConfig?.publishableKey && (
          <Elements stripe={stripePromise}>
            <SubscriptionCheckout
              plans={stripeConfig?.plans || null}
              tiers={stripeConfig?.tiers || null}
              defaultPlan={(stripeConfig?.defaultPlan as PlanKey) || 'annual'}
            />
          </Elements>
        )}
      </div>

      <ConfirmationModal
        isOpen={showCancelConfirm}
        title={t`Cancel Trial`}
        message={t`Cancel your free trial now? You'll keep access until your trial ends, and your card won't be charged.`}
        confirmText={t`Cancel Trial`}
        cancelText={t`Keep Trial`}
        onConfirm={confirmCancelSubscription}
        onCancel={() => setShowCancelConfirm(false)}
        variant="danger"
      />
    </Layout>
  );
}
